import { Mutation } from "@apollo/client/react/components";
import * as palette from "@govlaunch/palette";
import Link from "next/link";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Toggle } from "react-powerplug";
import ReactivateSubscription from "~/lib/mutations/ReactivateSubscription";
import {
  IReactivateSubscriptionMutation,
  IReactivateSubscriptionMutationVariables,
} from "~/lib/mutations/__generated__/ReactivateSubscription.generated";
import { ICompanyAccountBillingInfoQuery } from "~/lib/queries/__generated__/CompanyAccountBillingInfoQuery.generated";
import mediaQueries from "~/lib/utils/mediaQueries";
import Strongify from "~/lib/utils/strongify";
import { useSelfie } from "~/components/auth/Auth";
import InviteModal from "~/components/invite/InviteModal";
import getIntercomTarget from "~/components/vendor/profile/getIntercomTarget";
import { UsersCanAccessAccountPageToggleRow } from "~/components/makers/UsersCanAccessAccountPage";
import { UsersCanRequestContentTranslationRow } from "~/components/makers/UsersRequestContentTranslation";

interface ICurrentPlanAndUsageProps {
  company: NonNullable<ICompanyAccountBillingInfoQuery["company"]>;
}

export default function CurrentPlanAndUsage({ company }: ICurrentPlanAndUsageProps) {
  const loggedInUser = useSelfie();
  const intl = useIntl();

  if (!loggedInUser) {
    return null;
  }

  const userHasOwnershipRights = loggedInUser.role === "COMPANY_OWNER" || company.usersCanAccessAccountPage;

  const Reactivate = () => {
    if (company.stripePlan) {
      return <FormattedMessage defaultMessage="Reactivate {name}" id="eMM95H" values={{ name: company.stripePlan.displayName }} />;
    }

    return <FormattedMessage defaultMessage="Subscription" id="R/6nsx" />;
  };

  return (
    <div
      data-intercom-target={getIntercomTarget(loggedInUser, {
        asMember: "Settings Account Box",
        asOwner: "Settings Account Box",
        asDisruptor: "Settings Account Box for Disruptors",
      })}
      css={{
        boxSizing: "border-box",
        padding: 24,
        backgroundColor: palette.white,
        border: "solid 0.5px #e6e8ea",
        borderRadius: 4,
      }}
    >
      <h1
        css={{
          margin: 0,
          color: "#212121",
          fontWeight: 600,
          fontSize: 20,
        }}
      >
        Account
      </h1>

      <hr
        css={{
          width: "100%",
          marginTop: 20,
          border: "none",
          borderBottom: `solid 1px #e6e8ea`,
        }}
      />

      <div
        css={{
          paddingTop: 20,
        }}
      >
        <div
          css={mediaQueries({
            display: "grid",
            gridTemplateColumns: ["1fr", "1fr", "1fr", "max-content 1fr"],
            gridColumnGap: 40,
            gridRowGap: 30,
          })}
        >
          <div
            css={{
              fontSize: 16,
              fontWeight: 600,
              color: "#949195",
            }}
          >
            <FormattedMessage defaultMessage="Current Plan" id="Z3GpJF" />
          </div>

          <div
            css={{
              display: "grid",
              gridRowGap: 10,
            }}
          >
            <strong
              css={{
                fontSize: 16,
                fontWeight: 600,
                color: palette.text,
              }}
            >
              {company.plan.displayName}
            </strong>

            {!company.isDisruptor && (
              <div
                css={{
                  display: "grid",
                  gridAutoFlow: "column",
                  gridAutoColumns: "max-content",
                  gridColumnGap: 10,
                }}
              >
                {company.subscription &&
                  company.subscription.__typename === "ActiveSubscription" &&
                  company.stripePlan && (
                    <Link
                      href="/vendors/[vendorSlug]/[[...content]]"
                      as={`/vendors/${company.slug}/change-plans`}
                      passHref={true}
                    >
                      <a
                        href="/makers"
                        css={{
                          color: "#005aee",
                          fontSize: 13,
                        }}
                      >
                        <FormattedMessage defaultMessage="Change Plans" id="D+Kmq3" />
                      </a>
                    </Link>
                  )}

                {!company.subscription && (
                  <Link href="/makers" passHref={true}>
                    <a
                      href="/makers"
                      css={{
                        color: "#005aee",
                        fontSize: 13,
                      }}
                    >
                      <FormattedMessage defaultMessage="View plans" id="EaucLA" />
                    </a>
                  </Link>
                )}

                {company.subscription && company.subscription.__typename === "ScheduledToCancelSubscription" && (
                  <Mutation<IReactivateSubscriptionMutation, IReactivateSubscriptionMutationVariables>
                    mutation={ReactivateSubscription}
                  >
                    {(reactivateSubscription, { loading }) => (
                      <button
                        type="button"
                        disabled={loading}
                        onClick={() => reactivateSubscription()}
                        css={{
                          display: "inline-flex",
                          boxSizing: "border-box",
                          margin: 0,
                          padding: 0,
                          color: "#005aee",
                          fontSize: 13,
                          backgroundColor: "transparent",
                          border: "none",
                          outline: "none",
                          cursor: loading ? "default" : "pointer",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {loading ? <FormattedMessage defaultMessage="Reactivating..." id="9QDqZs" /> : <Reactivate />}
                      </button>
                    )}
                  </Mutation>
                )}
              </div>
            )}
          </div>

          <div
            css={{
              fontSize: 16,
              fontWeight: 600,
              color: "#949195",
            }}
          >
            <FormattedMessage defaultMessage="Team Members" id="HCYBYT" />
          </div>

          <div>
            <UsageCapLine usage={company.planUsage.teamMembers} cap={company.plan.limits.teamMembers} />

            {userHasOwnershipRights && (
              <Toggle>
                {({ on: isOpen, toggle }) => (
                  <>
                    <span
                      role="button"
                      onClick={toggle}
                      css={{
                        cursor: "pointer",
                        color: "#005aee",
                        fontSize: 13,
                      }}
                    >
                      <FormattedMessage defaultMessage="Add more teammates" id="vCvY6V" />
                    </span>

                    <InviteModal
                      title={intl.formatMessage({
                        defaultMessage: 'Invite Team Members', id: 'LeF5J5',
                      })}
                      subtitle={
                        <span>
                          <FormattedMessage
                            defaultMessage="They’ll be granted access to your <strong>Vendor profile</strong>" id="u9sOgv"
                            values={{
                              strong: (chunk: any) => Strongify(chunk),
                            }}
                          />
                        </span>
                      }
                      targetType="VENDOR"
                      targetId={company._id}
                      isOpen={isOpen}
                      onRequestClose={toggle}
                    />
                  </>
                )}
              </Toggle>
            )}
          </div>

          <div
            css={{
              fontSize: 16,
              fontWeight: 600,
              color: "#949195",
            }}
          >
            <FormattedMessage defaultMessage="Products" id="7NFfmz" />
          </div>

          <UsageCapLine usage={company.planUsage.products} cap={company.plan.limits.products} />

          <div
            css={{
              fontSize: 16,
              fontWeight: 600,
              color: "#949195",
            }}
          >
            <FormattedMessage defaultMessage="Projects (month)" id="ZTV6Lo" />
          </div>

          <UsageCapLine usage={company.planUsage.projectsThisMonth} cap={company.plan.limits.projectsPerMonth} />

          <div
            css={{
              fontSize: 16,
              fontWeight: 600,
              color: "#949195",
            }}
          >
            <FormattedMessage defaultMessage="Maker Products" id="LGAD29" />
          </div>

          <UsageCapLine usage={company.planUsage.enrichedProducts} cap={company.plan.limits.enrichedProducts} />

          <div
            css={{
              fontSize: 16,
              fontWeight: 600,
              color: "#949195",
            }}
          >
            <FormattedMessage defaultMessage="Groups" id="hzmswI" />
          </div>

          <UsageCapLine usage={company.planUsage.groups} cap={company.plan.limits.groups} />

          <div
            css={{
              fontSize: 16,
              fontWeight: 600,
              color: "#949195",
            }}
          >
            <FormattedMessage defaultMessage="Sharing Hub" id="88oiPf" />
          </div>

          <div
            css={{
              display: "grid",
              gridColumnGap: 12,
              gridTemplateColumns: "repeat(2, max-content)",
              alignItems: "center",
              color: palette.text,
              fontSize: 16,
              "& > strong": {
                fontWeight: 600,
              },
            }}
          >
            {company.plan.limits.sharingPage ? (
              <strong>
                <FormattedMessage defaultMessage="Included" id="ULRaOP" />
              </strong>
            ) : (
              <strong>
                <FormattedMessage defaultMessage="Not available" id="cPhKTw" />
              </strong>
            )}

            {company.planUsage.sharingPage ? (
              <span
                css={{
                  padding: "4px 12px",
                  color: palette.disruptiveGreen,
                  fontWeight: 600,
                  fontSize: 14,
                  backgroundColor: palette.lightestGreen,
                  borderRadius: 12,
                }}
              >
                <FormattedMessage defaultMessage="Enabled" id="V52jNn" />
              </span>
            ) : (
              <span
                css={{
                  padding: "4px 12px",
                  color: palette.red,
                  fontWeight: 600,
                  fontSize: 14,
                  backgroundColor: palette.lightRed,
                  borderRadius: 12,
                }}
              >
                <FormattedMessage defaultMessage="Disabled" id="tthToS" />
              </span>
            )}
          </div>

          <div
            css={{
              fontSize: 16,
              fontWeight: 600,
              color: "#949195",
            }}
          >
            <FormattedMessage defaultMessage="Invite Govts." id="2UI1Ed" />
          </div>

          <div
            css={{
              display: "grid",
              gridColumnGap: 12,
              gridTemplateColumns: "repeat(2, max-content)",
              alignItems: "center",
              color: palette.text,
              fontSize: 16,
              "& > strong": {
                fontWeight: 600,
              },
            }}
          >
            {company.plan.limits.inviteGovernments ? (
              <strong>
                <FormattedMessage defaultMessage="Included" id="ULRaOP" />
              </strong>
            ) : (
              <strong>
                <FormattedMessage defaultMessage="Not available" id="cPhKTw" />
              </strong>
            )}
          </div>
          {loggedInUser.role === "COMPANY_OWNER" && (
            <UsersCanAccessAccountPageToggleRow
              usersCanAccessAccountPage={company.usersCanAccessAccountPage}
              vendorId={company._id}
            />
          )}
          <UsersCanRequestContentTranslationRow
            suggestMyContentTranslation={company.usersCanRequestContentTranslation}
            vendorId={company._id}
          />
        </div>
      </div>
    </div>
  );
}

interface IUsageCapLineProps {
  usage: number;
  cap: number;
}

function UsageCapLine({ usage, cap }: IUsageCapLineProps) {
  return (
    <div
      css={{
        fontSize: 16,
        color: palette.text,
        "& > strong": {
          fontWeight: 600,
        },
      }}
    >
      <FormattedMessage
        defaultMessage="<span>{usage}</span> of <strong>{cap}</strong>" id="KtgCK9"
        values={{
          usage,
          cap,
          span: (text: string) => <span>{text}</span>,
          strong: (text: string) => <strong>{text}</strong>,
        }}
      />
    </div>
  );
}
